import React from 'react';
import { MarketGroup } from 'src/domains/sportsbook/webview/components/eventMarkets/marketGroup/MarketGroups';
import { observer } from 'src/utils/mobx-react';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { EventMarketsWrapper } from './EventsMarkets.style';
import { useAppStateContext } from 'src/appState/AppState';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { groupMarketsClassic, groupMarketsUnified, sortMarketGroups } from './groupMarkets';
import { GroupedEventMarkets } from './groupMarkets/shared';
import { LineMarketSlotCoalesceState } from './lineMarketSlotCoalesce/LineMarketSlotCoalesceState';
import {
    getLineMarketSlotCoalesceMaxSlots,
    shouldEnableLineMarketSlotCoalesce,
} from './lineMarketSlotCoalesce/lineMarketSlotCoalesceConfig';

interface PropsType {
    eventId: EventId;
    markets: Array<EventMarketItemType>;
}

const EventMarketsList = observer(
    'EventMarketsList',
    (props: {
        eventId: EventId;
        markets: GroupedEventMarkets;
        eventRowVariant: DefaultComponentVariantType | null;
        suspendedMarketIds?: ReadonlySet<number>;
    }) => {
        const { eventId, markets, eventRowVariant, suspendedMarketIds } = props;

        return (
            <EventMarketsWrapper variant={eventRowVariant}>
                {sortMarketGroups(markets).map((type, index) => {
                    const marketsByType = markets[type];
                    const marketType = type.includes('-ungrouped-') ? type.split('-ungrouped-')[0] : type;

                    if (marketsByType === undefined || marketType === undefined) {
                        return null;
                    }

                    return (
                        <MarketGroup
                            key={type}
                            order={index}
                            type={marketType}
                            markets={marketsByType}
                            eventId={eventId}
                            suspendedMarketIds={suspendedMarketIds}
                        />
                    );
                })}
            </EventMarketsWrapper>
        );
    }
);

/** Classic path — raw markets, no line-slot coalesce. */
const EventMarketsClassic = observer('EventMarketsClassic', (props: PropsType) => {
    const {
        componentVariants: { eventRowVariant },
    } = useAppStateContext().unifiedComponentConfig;

    return (
        <EventMarketsList
            eventId={props.eventId}
            markets={groupMarketsClassic(props.markets)}
            eventRowVariant={eventRowVariant}
        />
    );
});

/** Unified path — optional sticky line slots via allowlist + 2863 suspended-line handling. */
const EventMarketsUnified = observer('EventMarketsUnified', (props: PropsType) => {
    const {
        componentVariants: { eventRowVariant },
    } = useAppStateContext().unifiedComponentConfig;

    const eventModel = props.eventId.getEventModel();
    const isInPlay = eventModel?.timeSettingsStarted === true;
    const coalesceEnabled = shouldEnableLineMarketSlotCoalesce(props.eventId);

    const [coalesce] = React.useState(() => {
        const state = new LineMarketSlotCoalesceState({
            maxSlotsPerGroup: getLineMarketSlotCoalesceMaxSlots(props.eventId),
        });
        if (shouldEnableLineMarketSlotCoalesce(props.eventId)) {
            state.sync(props.markets);
        }
        return state;
    });

    React.useLayoutEffect(() => {
        if (coalesceEnabled === false) {
            return;
        }
        coalesce.sync(props.markets);
    }, [coalesce, coalesceEnabled, props.markets]);

    React.useEffect(() => {
        return () => {
            coalesce.dispose();
        };
    }, [coalesce]);

    const marketsForGrouping = coalesceEnabled ? coalesce.viewMarkets : props.markets;
    const suspendedMarketIds = coalesceEnabled ? coalesce.suspendedMarketIds : undefined;

    return (
        <EventMarketsList
            eventId={props.eventId}
            markets={groupMarketsUnified(marketsForGrouping, {
                isInPlay,
                // lineGroupMinCount: coalesceEnabled ? 1 : 2,
                lineGroupMinCount: 1,
            })}
            eventRowVariant={eventRowVariant}
            suspendedMarketIds={suspendedMarketIds}
        />
    );
});

export const EventMarkets = observer('EventMarkets', (props: PropsType) => {
    const { unifiedSportEventsTable } = useAppStateContext().unifiedComponentConfig;

    if (unifiedSportEventsTable === false) {
        return <EventMarketsClassic {...props} />;
    }

    return <EventMarketsUnified {...props} />;
});
